import type { ReactNode } from 'react';
import { Button, Typography } from 'antd';
import { ExclamationCircleOutlined, ReloadOutlined } from '@ant-design/icons';

const { Text } = Typography;

export interface ErrorStateProps {
  /** WHAT failed, in the operator's words — "the run index", "this hub's flows". Never "data". */
  what: string;
  /** The server's own message, verbatim. Shown as given; never rewritten into reassurance. */
  detail?: string | null;
  onRetry?: () => void;
  /** Extra affordance (e.g. a link to the surface that owns the failing dependency). */
  extra?: ReactNode;
  /** Compact = inside a table body; otherwise it stands alone on the page. */
  compact?: boolean;
}

/**
 * The one load-failure state (OPSFL-POLISH-1 designed-states pass).
 *
 * A toast that has already faded is not an error state: the operator is left looking at an empty
 * table with no way to tell "nothing matched" from "the call failed", and no way to retry but a
 * full page reload. This names the thing that failed, quotes the server verbatim (honest
 * reporting — never a friendly paraphrase of a 500), and puts Retry under the cursor.
 *
 * `role="alert"` so a failure that lands while the operator is looking elsewhere is announced.
 */
export function ErrorState({ what, detail, onRetry, extra, compact }: ErrorStateProps) {
  return (
    <div
      role="alert"
      style={{
        padding: compact ? '18px 12px' : '32px 16px',
        textAlign: 'center',
        maxWidth: 520,
        margin: '0 auto',
      }}
    >
      <ExclamationCircleOutlined
        aria-hidden
        style={{ fontSize: compact ? 20 : 28, color: 'var(--ol-negative)' }}
      />
      <div style={{ marginTop: 8, fontWeight: 600, fontSize: 13.5 }}>Could not load {what}</div>
      {detail && (
        <Text
          type="secondary"
          style={{ display: 'block', marginTop: 6, fontSize: 12, lineHeight: 1.5 }}
        >
          {detail}
        </Text>
      )}
      <div style={{ marginTop: 12, display: 'flex', gap: 8, justifyContent: 'center' }}>
        {onRetry && (
          <Button size="small" icon={<ReloadOutlined />} onClick={onRetry}>
            Retry
          </Button>
        )}
        {extra}
      </div>
    </div>
  );
}
